// ── ADMIN: REGISTRATIONS QUEUE + ENROLLMENT STATUS ─────────────────────
// Two views sharing one nav item (VIEWS['a-reg-applications'] = the Pending/
// Approved/Waitlisted/Declined queue, VIEWS['a-reg-enrollment'] = the
// per-program enrollment-status matrix via renderPipeline()), plus the
// review/decision logic both depend on. renderPipeline/setReviewDecision/
// submitReview/buildContractsForApproved/decideWaitlisted/
// applyWaitlistDecision were physically located under legacy-monolith.js's
// "CONTRACT SIGNATURE → MATERIALS FEE" header, but that header turned out to
// have no actual logic of its own (its comment describes server-side/Firma-
// webhook behavior) — grepped for materialsFee/markSigned/signContract and
// found nothing besides the comment, so everything under it that had real
// code belonged here instead.
//
// esc/ageAsOfSept1 (shared classic scripts) stay bare globals. sendContract/
// remindContract/declineApprovedContract (admin-contracts.js) are real
// imports, called from renderPipeline()'s action buttons below and wired
// via VIEW_INIT['a-reg-enrollment'] — that's the only VIEWS key that ever
// renders renderPipeline()'s output (regMode === 'enrollment'), so
// 'a-reg-applications' doesn't need the same wiring.
//
// A view-only admin gets Enrollment but not Registration (see NAV in
// core/routing.js): the enrollment matrix is the PC/BB roster they need, while
// the Registration review queue is nothing but approve/waitlist/decline. Both renders still
// canEdit(session)-gate their write buttons -- the two views share one render
// function and a hash like #a-reg-applications is typeable regardless of what
// the nav lists. The printable contact list stays available to view-only, since
// calling a family about a missed rehearsal is exactly their job.
//
// VIEW_INIT['a-reg-applications'] wires the tab/search controls and calls
// wireRegList() for #regList's buttons (submit-review, per-performer
// decision, waitlist approve/decline) — regSetSearch()/regClearSearch()
// rewrite #regList directly (to keep focus in the search box while typing),
// bypassing route()/VIEW_INIT, so wireRegList() is also called after that
// direct rewrite, same wireXGroup shape as every other domain. VIEW_INIT
// ['a-reg-enrollment'] additionally wires renderPipeline()'s sort/program/
// view-toggle controls.
import { apiFetch } from '../core/api.js';
import { DB, PROGRAMS, currentView, session } from '../core/state.js';
import { TODAY, fmtLocalDT } from '../core/format.js';
import { eligiblePrograms, isArchived, acctPhone, canEdit } from '../core/domain-utils.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, refresh } from '../core/routing.js';
import { sendContract, remindContract, declineApprovedContract } from './admin-contracts.js';

let regStatusFilter = 'pending';
let regSearch = '';
let reviewDraft = {};  // regId → { childIndex → decision } during batch review

function regSetStatusFilter(key) { regStatusFilter = key; refresh(); }

// Small performer glyph shown before each performer's name
function performerIcon() {
  return `<span style="flex-shrink:0;width:24px;height:24px;border-radius:50%;background:rgba(201,168,76,0.1);border:1px solid var(--border);display:inline-flex;align-items:center;justify-content:center">
    <svg aria-hidden="true" viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="var(--gold)" stroke-width="1.4"><circle cx="8" cy="5" r="2.6"/><path d="M3 13.5c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5"/></svg>
  </span>`;
}
// Program pill with a program-specific complementary color (distinct from action buttons).
// Exported for admin-accounts.js/parent-dashboard.js/parent-family.js's real imports.
const PROGRAM_PILL_COLORS = {
  company: { fg: '#7fb5e6', bd: 'rgba(127,181,230,0.4)', bg: 'rgba(127,181,230,0.1)' },  // soft blue
  bway:    { fg: '#c89bd6', bd: 'rgba(200,155,214,0.4)', bg: 'rgba(200,155,214,0.1)' }    // soft violet
};

// ── Returning-family flags ──────────────────────────────────────────────────
// `existingFamily` comes from RegistrationRoutes::matchExistingFamily(), which
// matches exactly the way post-signature account creation will: account by
// email, performer by account + name + date of birth. The point is that an
// archived family must not arrive in the Pending queue looking brand new — it
// changes what happens on approval (their old account, charges, and contract
// history come back) and it means the new-family age minimum is the wrong rule
// to judge them by.

const returningPill = r => {
  const ef = r.existingFamily;
  if (!ef) return '';
  return ef.archived
    ? '<span class="pill pill-amber" style="font-size:10px;margin-left:8px">Returning · archived</span>'
    : '<span class="pill pill-off" style="font-size:10px;margin-left:8px">Existing family</span>';
};

const returningNote = r => {
  const ef = r.existingFamily;
  if (!ef || !ef.archived) return '';
  const when = ef.archivedAt ? ` on ${fmtLocalDT(ef.archivedAt)}` : '';
  const why  = ef.archiveReason ? ` — ${esc(ef.archiveReason)}` : '';
  return `<div style="font-size:11px;color:var(--gold);margin-top:5px;line-height:1.5">
    This family was archived${when}${why}. Approving brings back their existing
    account and history once the contract is signed — no new account is created.
  </div>`;
};

const returningPerformerPill = (r, ch) => {
  const match = r.existingFamily && r.existingFamily.performers && r.existingFamily.performers[ch.id];
  if (!match) return '';
  return match.archived
    ? '<span class="pill pill-amber" style="font-size:10px">Archived performer</span>'
    : '<span class="pill pill-off" style="font-size:10px">On file</span>';
};

// Same palette as programPill, applied to arbitrary text -- the parent
// dashboard uses it for performer-name chips so a child reads as belonging to
// the program group above them. Falls back to the neutral muted border for
// Classes and workshops, which have no assigned colour.
export function programChip(programId, text) {
  const c = PROGRAM_PILL_COLORS[programId] || { fg: 'var(--muted)', bd: 'var(--border-soft)', bg: 'transparent' };
  return `<span style="flex-shrink:0;font-size:12px;padding:3px 11px;border-radius:20px;border:1px solid ${c.bd};background:${c.bg};color:${c.fg};white-space:nowrap">${esc(text)}</span>`;
}

export function programPill(programId) {
  const p = PROGRAMS.find(x => x.id === programId);
  const name = p ? p.name : 'Unknown program';
  const c = PROGRAM_PILL_COLORS[programId] || { fg: 'var(--muted)', bd: 'var(--border-soft)', bg: 'transparent' };
  return `<span style="flex-shrink:0;font-size:11px;padding:3px 10px;border-radius:20px;border:1px solid ${c.bd};background:${c.bg};color:${c.fg};white-space:nowrap">${esc(name)}</span>`;
}

const renderRegistration = () => {
  // Mode is driven by the active nav sub-link
  const regMode = currentView === 'a-reg-enrollment' ? 'enrollment' : 'applications';
  const upSeason = DB.settings.upcomingSeasonYear;
  const appStatusMatches = (r, key) => {
    if (key === 'pending')    return r.children.some(c => c.status === 'pending');
    if (key === 'waitlisted') return !r.children.some(c => c.status === 'pending') && r.children.some(c => c.status === 'waitlisted');
    if (key === 'approved')   return !r.children.some(c => c.status === 'pending') && r.children.some(c => c.status === 'approved');
    if (key === 'declined')   return r.children.every(c => c.status === 'declined');
    return false;
  };
  const counts = {
    pending:    DB.registrations.filter(r => appStatusMatches(r, 'pending')).length,
    approved:   DB.registrations.filter(r => appStatusMatches(r, 'approved')).length,
    waitlisted: DB.registrations.filter(r => appStatusMatches(r, 'waitlisted')).length,
    declined:   DB.registrations.filter(r => appStatusMatches(r, 'declined')).length
  };
  const q = regSearch.trim().toLowerCase();
  const matchesSearch = r => {
    if (!q) return true;
    const hay = [
      r.parent.firstName, r.parent.lastName, r.parent.email,
      ...r.children.map(c => (c.firstName || '') + ' ' + (c.lastName || '') + ' ' + (c.name || ''))
    ].join(' ').toLowerCase();
    return hay.includes(q);
  };
  const filtered = DB.registrations
    .filter(r => appStatusMatches(r, regStatusFilter))
    .filter(matchesSearch)
    .sort((a,b) => a.submittedAt.localeCompare(b.submittedAt));

  const tab = (key, label) => `
    <button data-action="reg-set-status-filter" data-key="${key}" style="padding:7px 14px;border-radius:7px;border:1px solid ${regStatusFilter===key?'rgba(201,168,76,0.4)':'var(--border-soft)'};background:${regStatusFilter===key?'rgba(201,168,76,0.08)':'transparent'};color:${regStatusFilter===key?'var(--gold-light)':'var(--muted)'};font-size:13px;font-family:inherit;cursor:pointer">
      ${label}${counts[key] ? ` <span style="opacity:0.7">(${counts[key]})</span>` : ''}
    </button>`;

  const childName = ch => esc(ch.firstName && ch.lastName ? ch.firstName + ' ' + ch.lastName : ch.name || '');
  const statusPill = s => {
    const cls = s === 'approved' ? 'pill-paid' : s === 'declined' ? 'pill-due' : s === 'waitlisted' ? 'pill-off' : 'pill-off';
    return `<span class="pill ${cls}" style="font-size:11px">${s}</span>`;
  };

  return `
    <div class="page-head">
      <h2 class="serif">${regMode === 'enrollment' ? 'Enrollment' : 'Registration'}</h2>
    </div>
    ${regMode === 'enrollment' ? renderPipeline() : `
    <div style="display:flex;gap:8px;margin-bottom:20px;flex-wrap:wrap">
      ${tab('pending','Pending')}
      ${tab('approved','Approved')}
      ${tab('waitlisted','Waitlisted')}
      ${tab('declined','Declined')}
    </div>
    ${(regStatusFilter !== 'pending') ? `
    <div class="field" style="margin-bottom:18px">
      <div style="position:relative">
        <input type="text" id="regSearchInput" placeholder="Search by family or performer name…" value="${esc(regSearch)}"
          data-action="reg-search-input" style="height:40px;padding-right:28px;width:100%;box-sizing:border-box">
        <button id="regClearBtn" data-action="reg-clear-search" aria-label="Clear search" style="position:absolute;right:6px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--muted);font-size:18px;line-height:1;padding:2px 4px;display:${regSearch ? 'block' : 'none'}">×</button>
      </div>
    </div>` : ''}
    <div id="regList">${
      (regStatusFilter === 'pending' ? filtered.slice(0, 1) : filtered).length ? (regStatusFilter === 'pending' ? filtered.slice(0, 1) : filtered).map((r, qIdx) => {
      const inReview = regStatusFilter === 'pending';   // batch-review mode in the Pending tab
      const draft = reviewDraft[r.id] || {};
      return `
      <div class="card" style="margin-bottom:14px;padding:18px">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;margin-bottom:10px">
          <div style="flex:1;min-width:240px">
            <div style="font-family:'Libre Baskerville',serif;font-size:16px;color:var(--cream)">${esc(r.parent.firstName)} ${esc(r.parent.lastName)}${returningPill(r)}</div>
            <div style="font-size:12px;color:var(--muted);margin-top:2px">${esc(r.parent.email)} · ${esc(r.parent.phone)}${r.parent.city ? ' · ' + esc(r.parent.city) + ', ' + esc(r.parent.state || '') : ''}</div>
            <div style="font-size:11px;color:var(--muted);margin-top:1px">Submitted ${fmtLocalDT(r.submittedAt)}</div>
            ${returningNote(r)}
          </div>
          ${inReview && canEdit(session) ? `<button class="btn" id="reviewBtn-${r.id}" data-action="submit-review" data-reg="${r.id}" style="width:auto;padding:9px 20px;flex-shrink:0">Submit decisions</button>` : ''}
        </div>
        ${inReview ? `<div id="reviewErr-${r.id}" style="display:none;font-size:12px;color:var(--red);margin-bottom:10px"></div>` : ''}
        <div style="padding-top:10px;border-top:1px solid var(--border-soft)">
          ${r.children.map((ch, ci) => {
            const age = ageAsOfSept1(ch.dob, upSeason);
            const reqProg = PROGRAMS.find(p => p.id === ch.programInterest);
            let ageFlag = '';
            if (reqProg) {
              const eligible = eligiblePrograms({ id: '_tmp', dob: ch.dob }, upSeason).some(p => p.id === reqProg.id);
              if (!eligible) ageFlag = `<span style="font-size:11px;color:#f87171;margin-left:8px">⚠ Age ${age} doesn't fit ${esc(reqProg.name)} as of Sept 1</span>`;
            }
            // Pending performers in review mode get decision toggles; decided ones show their status
            const decision = draft[ci] || (ch.status === 'pending' ? '' : ch.status);
            const decisionBtn = (val, label, color) => `
              <button data-action="set-review-decision" data-reg="${r.id}" data-child="${ci}" data-decision="${val}" style="padding:5px 11px;border-radius:6px;font-size:12px;font-family:inherit;cursor:pointer;border:1px solid ${decision===val ? color : 'var(--border-soft)'};background:${decision===val ? color.replace('1)','0.12)').replace('rgb','rgba') : 'transparent'};color:${decision===val ? color : 'var(--muted)'}">${label}</button>`;
            return `
            <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;padding:7px 0;${ci < r.children.length-1 ? 'border-bottom:1px solid var(--border-soft)' : ''}">
              <div style="flex:1;min-width:0;display:flex;align-items:center;gap:9px">
                ${performerIcon()}
                <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
                  <span style="font-size:14px;color:var(--cream)">${childName(ch)}<span style="font-size:12px;color:var(--muted);margin-left:8px">${ch.gender ? esc(ch.gender) + ' · ' : ''}age ${age} on Sept 1${ch.grade ? ' · ' + esc(ch.grade) + ' grade' : ''}${ch.school ? ' · ' + esc(ch.school) : ''}${ageFlag}</span></span>
                  ${programPill(ch.programInterest)}
                  ${returningPerformerPill(r, ch)}
                </div>
              </div>
              ${!canEdit(session)
                ? `<div style="flex-shrink:0">${statusPill(ch.status)}</div>`
                : ch.status === 'pending'
                ? `<div style="display:flex;gap:6px;flex-shrink:0">
                     ${decisionBtn('approved','Approve','rgb(52,211,153,1)')}
                     ${decisionBtn('waitlisted','Waitlist','rgb(201,168,76,1)')}
                     ${decisionBtn('declined','Decline','rgb(248,113,113,1)')}
                   </div>`
                : (regStatusFilter === 'waitlisted' && ch.status === 'waitlisted')
                ? `<div style="display:flex;align-items:center;gap:10px;flex-shrink:0">
                     ${statusPill(ch.status)}
                     <button class="btn-ghost btn-sm" data-action="decide-waitlisted" data-reg="${r.id}" data-child="${ci}" data-decision="approved" style="border-color:rgba(52,211,153,0.4);color:#34d399">Approve</button>
                     <button class="btn-ghost btn-sm btn-danger" data-action="decide-waitlisted" data-reg="${r.id}" data-child="${ci}" data-decision="declined">Decline</button>
                   </div>`
                : `<div style="flex-shrink:0">${statusPill(ch.status)}</div>`}
            </div>`;
          }).join('')}
        </div>
      </div>`;
    }).join('') : `<div class="card"><p class="empty">${
      regStatusFilter === 'pending' ? 'All caught up — no pending registrations to review. <a onclick="route(&#39;a-reg-enrollment&#39;)" style="color:var(--gold);cursor:pointer">Check out enrollment so far.</a>'
      : regSearch.trim() ? 'No matches for "' + esc(regSearch.trim()) + '".'
      : 'No ' + regStatusFilter + ' registrations.'
    }</p></div>`
    }</div>
    ${regStatusFilter === 'pending' && filtered.length ? `<div style="font-size:13px;color:var(--muted);text-align:center;margin-top:4px">Reviewing in the order received — <span style="color:var(--gold-light)">1 of ${filtered.length}</span> pending</div>` : ''}`}`;
};
VIEWS['a-reg-applications'] = renderRegistration;
VIEWS['a-reg-enrollment']   = renderRegistration;

VIEW_INIT['a-reg-applications'] = () => {
  document.querySelectorAll('[data-action="reg-set-status-filter"]').forEach(btn => {
    btn.addEventListener('click', () => regSetStatusFilter(btn.dataset.key));
  });
  const searchEl = document.getElementById('regSearchInput');
  if (searchEl) searchEl.addEventListener('input', () => regSetSearch(searchEl.value));
  const clearBtn = document.getElementById('regClearBtn');
  if (clearBtn) clearBtn.addEventListener('click', regClearSearch);
  wireRegList();
};
// Wires #regList's dynamic action buttons — called from VIEW_INIT above for
// the initial render, and from regSearchUpdate() after its direct #regList
// rewrite (which bypasses route()/VIEW_INIT to keep focus in the search box).
function wireRegList() {
  const list = document.getElementById('regList');
  if (!list) return;
  list.querySelectorAll('[data-action="submit-review"]').forEach(btn => {
    btn.addEventListener('click', () => submitReview(btn.dataset.reg));
  });
  list.querySelectorAll('[data-action="set-review-decision"]').forEach(btn => {
    btn.addEventListener('click', () => setReviewDecision(btn.dataset.reg, parseInt(btn.dataset.child), btn.dataset.decision));
  });
  list.querySelectorAll('[data-action="decide-waitlisted"]').forEach(btn => {
    btn.addEventListener('click', () => decideWaitlisted(btn.dataset.reg, parseInt(btn.dataset.child), btn.dataset.decision));
  });
}

VIEW_INIT['a-reg-enrollment'] = () => {
  document.querySelectorAll('[data-action="send-contract"]').forEach(btn => {
    btn.addEventListener('click', () => sendContract(btn.dataset.family, btn.dataset.program));
  });
  document.querySelectorAll('[data-action="remind-contract"]').forEach(btn => {
    btn.addEventListener('click', () => remindContract(btn.dataset.family, btn.dataset.program));
  });
  document.querySelectorAll('[data-action="decline-contract"]').forEach(btn => {
    btn.addEventListener('click', () => declineApprovedContract(btn.dataset.family, btn.dataset.program));
  });
  document.querySelectorAll('[data-action="es-set-sort"]').forEach(th => {
    th.addEventListener('click', () => esSetSort(th.dataset.col));
  });
  document.querySelectorAll('[data-action="es-set-program"]').forEach(btn => {
    btn.addEventListener('click', () => esSetProgramId(btn.dataset.program));
  });
  document.querySelectorAll('[data-action="es-set-view"]').forEach(btn => {
    btn.addEventListener('click', () => esSetView(btn.dataset.view));
  });
  document.querySelectorAll('[data-action="es-print-contacts"]').forEach(btn => {
    btn.addEventListener('click', printCurrentContactList);
  });
};

// Re-render only the registration list so the search box keeps focus while typing.
function regSetSearch(val) {
  regSearch = val;
  const btn = document.getElementById('regClearBtn');
  if (btn) btn.style.display = val ? 'block' : 'none';
  regSearchUpdate();
}
function regClearSearch() {
  regSearch = '';
  const inp = document.getElementById('regSearchInput');
  if (inp) { inp.value = ''; inp.focus(); }
  const btn = document.getElementById('regClearBtn');
  if (btn) btn.style.display = 'none';
  regSearchUpdate();
}
function regSearchUpdate() {
  const fn = VIEWS[currentView] || renderRegistration;
  // Rebuild the whole view's HTML but transplant just the list portion.
  const tmp = document.createElement('div');
  tmp.innerHTML = fn();
  const fresh = tmp.querySelector('#regList');
  const current = document.getElementById('regList');
  if (fresh && current) current.replaceWith(fresh);
  wireRegList();
}

// ── ENROLLMENT STATUS VIEW (per-program status matrix) ───────────────
let esProgramId = '';                          // selected program (first program by default)
let esSort = { col: 'contract', dir: 'asc' };  // active sort column + direction
let esView = 'status';                         // 'status' | 'groups' (PC only)

function esSetProgramId(id) { esProgramId = id; refresh(); }
function esSetView(v) { esView = v; refresh(); }

// Performance Company age groups (age as of Sept 1)
const PC_AGE_GROUPS = [
  { key: 'youngest', label: 'Youngest Group', min: 8,  max: 10 },
  { key: 'middle',   label: 'Middle Group',   min: 11, max: 13 },
  { key: 'oldest',   label: 'Oldest Group',   min: 14, max: 18 }
];

function gatherEnrollmentRows() {
  const upSeason = DB.settings.upcomingSeasonYear;
  const rows = [];
  // 1) New families — approved performers from open-enrollment registrations
  const regPerformerKeys = new Set();
  DB.registrations.forEach(r => {
    r.children.filter(c => c.status === 'approved').forEach(ch => {
      const fullName = ch.firstName + ' ' + ch.lastName;
      regPerformerKeys.add(fullName + '|' + ch.programInterest);
      const newAcct = DB.users.find(u => u.email && u.email.toLowerCase() === (r.parent.email || '').toLowerCase());
      const newAcctId = newAcct ? newAcct.id : ((r.contracts || []).find(c => c.accountId)?.accountId ?? null);
      const newPerf = newAcctId ? DB.children.find(k => k.accountId === newAcctId && k.firstName === ch.firstName && k.lastName === ch.lastName) : null;
      // A family can have a signed contract and a pending one for the same
      // program (a sibling approved off the waitlist after they signed). A child
      // with a performer record was enrolled by the signed one; one without is
      // waiting on the pending one.
      const forProgram = (r.contracts || []).filter(c => c.programId === ch.programInterest);
      const contract = forProgram.find(c => newPerf ? c.signed : !c.signed) || forProgram[0];
      // These rows come from the frozen registration snapshot, which archiving
      // never touches -- so an approved performer who was archived before
      // signing would sit in the pipeline forever without this. A null newPerf
      // means no performer record exists yet (pre-signature), which is a normal
      // in-flight state, not an archived one.
      if (newPerf && isArchived(newPerf)) return;
      rows.push({
        name: fullName,
        family: `${r.parent.firstName} ${r.parent.lastName}`,
        programId: ch.programInterest, source: 'New', approved: true,
        // Prefer the live performer record over the frozen registration snapshot once one
        // exists — e.g. a corrected DOB after approval should be reflected here too.
        gender: (newPerf ? newPerf.gender : ch.gender) || '',
        dob: newPerf ? newPerf.dob : ch.dob,
        age: ageAsOfSept1(newPerf ? newPerf.dob : ch.dob, upSeason),
        contractSent: !!(contract && contract.sent),
        contractSigned: !!(contract && contract.signed),
        feePaid: (newPerf ? DB.charges.some(c => c.childId === newPerf.id && c.programId === ch.programInterest && c.type === 'One-time' && c.status === 'paid') : false) || !!r.paymentReceived,
        // Prefer the account's phone once one exists (a parent can correct it in
        // the portal); the registration snapshot is the fallback for families
        // who haven't signed yet and so have no account row.
        phone: (newAcct && newAcct.phone) || r.parent.phone || '',
        accountId: newAcctId, performerId: newPerf?.id ?? null,
        sentDate: r.reviewedAt ?? null,
        lastRemindedAt: contract?.lastRemindedAt ?? null,
        familyKey: 'reg:' + r.id, regId: r.id
      });
    });
  });
  // 2) Returning families — seasonEnrollments for the upcoming season
  // Skip enrollments that originated from a registration (already shown above with contract status)
  DB.seasonEnrollments.filter(se => se.seasonYear === upSeason).forEach(se => {
    const perf = DB.children.find(c => c.id === se.performerId);
    if (!perf) return;
    // Archiving deliberately leaves enrollments intact so a returning performer
    // reconnects to her own history, which means the enrollment alone can't be
    // trusted to say who's still in the season -- the performer record has to be
    // consulted too.
    if (isArchived(perf)) return;
    if (regPerformerKeys.has(perf.name + '|' + se.programId)) return;
    const acct = DB.users.find(u => u.id === perf.accountId);
    const contract = (DB.contracts || []).find(c => c.accountId === perf.accountId && c.programId === se.programId && c.seasonYear === upSeason);
    rows.push({
      name: perf.name,
      family: acct ? `${acct.firstName} ${acct.lastName}` : '—',
      programId: se.programId, source: 'Returning', approved: true,
      gender: perf.gender || '', dob: perf.dob, age: ageAsOfSept1(perf.dob, upSeason),
      contractSent: !!(contract && contract.firmaSigningRequestId), contractSigned: !!(contract && contract.signed),
      feePaid: DB.charges.some(c => c.childId === se.performerId && c.programId === se.programId && c.type === 'One-time' && c.status === 'paid'),
      phone: acctPhone(perf.accountId),
      seId: se.id, accountId: perf.accountId, performerId: perf.id,
      sentDate: contract?.createdAt ?? null,
      lastRemindedAt: contract?.lastRemindedAt ?? null,
      familyKey: 'acct:' + perf.accountId
    });
  });
  return rows;
}

function esSortKey(r, col) {
  switch (col) {
    case 'name':     return r.name.toLowerCase();
    case 'family':   return r.family.toLowerCase();
    case 'source':   return r.source;
    case 'approved': return r.approved ? 1 : 0;
    case 'contract': return r.contractSigned ? 2 : (r.contractSent ? 1 : 0);
    case 'fee':      return r.feePaid ? 1 : 0;
    case 'sent':     return r.sentDate ? new Date(r.sentDate).getTime() : 0;
    default:         return r.name.toLowerCase();
  }
}
function esSetSort(col) {
  if (esSort.col === col) esSort.dir = esSort.dir === 'asc' ? 'desc' : 'asc';
  else { esSort.col = col; esSort.dir = 'asc'; }
  refresh();
}

// Admin marks a contract signed. When ALL of a family's contracts for the upcoming season
// are signed, one consolidated, itemized materials-fee charge is billed as `due` and a
// "materials fee due" email is sent. The family pays via the portal (balance banner / Billing).
// In production the Firma webhook drives this — never a manual admin action.
// ── PRINTABLE CONTACT LIST ───────────────────────────────────────────
// For the staff member who calls home when a performer misses rehearsal.
// It lives here rather than on the Performers page precisely because this
// page is already one program at a time: the printout is whatever program
// is selected, so a PC sheet and a BB sheet are two separate print jobs and
// can never come out interleaved.
//
// One list, always the same shape -- deliberately independent of the By
// Status / By Age Group toggle, which is about reading the enrollment
// pipeline on screen, not about what a call-down sheet should contain.
//
// Opens a clean, light-themed document rather than printing the dark admin
// UI, same approach as the merch store's packing slips.
function printablePhone(raw) {
  const digits = (raw || '').replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return (raw || '').trim();
}
// A-Z by the performer's FIRST name -- how she knows the kids -- with the
// full name as a stable tiebreak for two Emmas.
const byFirstName = (a, b) =>
  a.name.split(' ')[0].localeCompare(b.name.split(' ')[0], undefined, { sensitivity: 'base' }) ||
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

function contactListRows(rows) {
  return rows.slice().sort(byFirstName).map(r => ({
    name: r.name,
    parent: r.family,
    phone: printablePhone(r.phone)
  }));
}

function printContactList(programName, rows) {
  const list = contactListRows(rows);
  if (!list.length) { toast('No performers to print for this program'); return; }
  const printed = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const sheetHtml = `
    <div class="sheet">
      <div class="sheet-hd">
        <div class="sheet-title">${esc(programName)}</div>
        <div class="sheet-sub">Contact list &middot; ${list.length} performer${list.length === 1 ? '' : 's'} &middot; ${esc(printed)}</div>
      </div>
      <table>
        <thead><tr><th>Performer</th><th>Parent</th><th>Phone</th></tr></thead>
        <tbody>${list.map(r => `<tr>
          <td class="who">${esc(r.name)}</td>
          <td>${esc(r.parent)}</td>
          <td class="tel">${r.phone ? esc(r.phone) : '<span class="none">no phone on file</span>'}</td>
        </tr>`).join('')}</tbody>
      </table>
    </div>`;
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self'; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com;">
  <title>Contact List — ${esc(programName)}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Source+Sans+3:wght@300;400;500;600&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Source Sans 3', sans-serif; font-weight: 300; color: #1a1a2e; background: #fff; }
    .sheet { padding: 40px; max-width: 720px; margin: 0 auto; }
    .sheet-hd { border-bottom: 2px solid #1a1a2e; padding-bottom: 10px; margin-bottom: 18px; }
    .sheet-title { font-family: 'Libre Baskerville', serif; font-size: 24px; font-weight: 700; }
    .sheet-sub { font-size: 11px; color: #9e9488; text-transform: uppercase; letter-spacing: 0.1em; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #9e9488; padding-bottom: 8px; border-bottom: 1px solid #e8e4dc; }
    td { padding: 9px 0; border-bottom: 1px solid #e8e4dc; font-size: 14px; vertical-align: top; }
    td.who { font-weight: 500; padding-right: 16px; }
    td.tel { white-space: nowrap; font-variant-numeric: tabular-nums; }
    td .none { color: #9e9488; font-style: italic; }
    tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
  </style></head><body>
  ${sheetHtml}
<script><\/script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) { toast('Allow pop-ups to print the contact list'); return; }
  w.document.write(html);
  w.document.close();
}

// The click handler recomputes from esProgramId rather than closing over
// renderPipeline()'s locals, so what prints is always the program selected
// now -- not the one selected when the page was last rendered.
function printCurrentContactList() {
  const prog = PROGRAMS.find(p => p.id === esProgramId);
  if (!prog) { toast('Pick a program first'); return; }
  printContactList(prog.name, gatherEnrollmentRows().filter(r => r.programId === esProgramId));
}

function renderPipeline() {
  const programs = PROGRAMS.filter(p => p.isProgram);
  if (!programs.some(p => p.id === esProgramId)) esProgramId = programs[0] ? programs[0].id : '';
  const prog = programs.find(p => p.id === esProgramId);
  const allRows = gatherEnrollmentRows().filter(r => r.programId === esProgramId);

  const total = allRows.length;
  const signed = allRows.filter(r => r.contractSigned).length;
  const paid = allRows.filter(r => r.feePaid).length;

  let rows = allRows.slice();
  rows.sort((a, b) => {
    const ka = esSortKey(a, esSort.col), kb = esSortKey(b, esSort.col);
    if (ka < kb) return esSort.dir === 'asc' ? -1 : 1;
    if (ka > kb) return esSort.dir === 'asc' ? 1 : -1;
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());  // stable tiebreak
  });

  const stageCell = (done, labelDone, labelPending) =>
    `<span class="pill ${done ? 'pill-paid' : 'pill-off'}" style="font-size:11px">${done ? labelDone : labelPending}</span>`;

  const arrow = col => esSort.col === col ? (esSort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const th = (col, label) => `<th data-action="es-set-sort" data-col="${col}" style="cursor:pointer;user-select:none;white-space:nowrap">${label}<span style="color:var(--gold);font-size:10px">${arrow(col)}</span></th>`;

  // Program switch buttons
  const progSwitch = programs.map(p => `
    <button data-action="es-set-program" data-program="${p.id}" style="padding:9px 18px;border-radius:8px;border:1px solid ${esProgramId===p.id?'rgba(201,168,76,0.45)':'var(--border-soft)'};background:${esProgramId===p.id?'rgba(201,168,76,0.1)':'transparent'};color:${esProgramId===p.id?'var(--gold-light)':'var(--muted)'};font-size:14px;font-family:inherit;cursor:pointer;font-weight:${esProgramId===p.id?'500':'300'}">
      ${esc(p.name)}
    </button>`).join('');

  // Glanceable summary cards — one per program, also act as the program selector
  const allRowsByProgram = gatherEnrollmentRows();
  const summaryCards = programs.map(p => {
    const pr = allRowsByProgram.filter(r => r.programId === p.id);
    const totalP = pr.length;
    const completed = pr.filter(r => r.contractSigned && r.feePaid).length;  // fully enrolled
    const pct = totalP ? Math.round((completed / totalP) * 100) : 0;
    const active = esProgramId === p.id;
    return `
      <button data-action="es-set-program" data-program="${p.id}" style="flex:1;min-width:220px;text-align:left;background:${active?'rgba(201,168,76,0.08)':'var(--bg-card)'};border:1px solid ${active?'rgba(201,168,76,0.45)':'var(--border-soft)'};border-radius:var(--r);padding:18px;cursor:pointer;font-family:inherit;transition:border-color 0.15s">
        <div style="font-family:'Libre Baskerville',serif;font-size:16px;color:${active?'var(--gold-light)':'var(--cream)'};margin-bottom:12px">${esc(p.name)}</div>
        <div style="display:flex;align-items:baseline;gap:8px;margin-bottom:10px">
          <span style="font-family:'Libre Baskerville',serif;font-size:26px;color:var(--cream)">${completed}</span>
          <span style="font-size:14px;color:var(--muted)">of ${totalP} complete${totalP > 0 ? ' (' + pct + '%)' : ''}</span>
        </div>
        <div style="height:5px;background:rgba(255,255,255,0.06);border-radius:3px;overflow:hidden">
          <div style="height:100%;width:${pct}%;background:var(--gold);border-radius:3px"></div>
        </div>
      </button>`;
  }).join('');
  const summaryRow = `<div style="display:flex;gap:14px;flex-wrap:wrap;margin-bottom:22px">${summaryCards}</div>`;

  // View toggle — only Performance Company has the age-group lens
  const isPC = (prog && prog.name || '').toLowerCase().includes('performance company');
  const viewToggle = isPC ? `
    <div style="display:flex;gap:6px">
      <button data-action="es-set-view" data-view="status" style="padding:7px 14px;border-radius:7px;border:1px solid ${esView==='status'?'rgba(201,168,76,0.4)':'var(--border-soft)'};background:${esView==='status'?'rgba(201,168,76,0.08)':'transparent'};color:${esView==='status'?'var(--gold-light)':'var(--muted)'};font-size:13px;font-family:inherit;cursor:pointer">By Status</button>
      <button data-action="es-set-view" data-view="groups" style="padding:7px 14px;border-radius:7px;border:1px solid ${esView==='groups'?'rgba(201,168,76,0.4)':'var(--border-soft)'};background:${esView==='groups'?'rgba(201,168,76,0.08)':'transparent'};color:${esView==='groups'?'var(--gold-light)':'var(--muted)'};font-size:13px;font-family:inherit;cursor:pointer">By Age Group</button>
    </div>` : '';

  // Printable per-program contact list for rehearsal call-downs. Sits opposite
  // the view toggle in both views, and prints whichever program is selected.
  const printBtn = `<button class="btn-ghost btn-sm" data-action="es-print-contacts" title="${esc(prog ? prog.name : '')} performers with parent name and phone">Print contact list</button>`;

  // Bulk contract actions for the selected program (computed once, used by both views).
  // ── By Age Group view (Performance Company only) ──
  if (isPC && esView === 'groups') {
    const groupPanel = g => {
      const members = allRows
        .filter(r => r.age !== null && r.age >= g.min && r.age <= g.max)
        .sort((a, b) => a.age - b.age || a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      const males = members.filter(m => m.gender === 'Male').length;
      const females = members.filter(m => m.gender === 'Female').length;
      const breakdown = [];
      if (females) breakdown.push(`${females} ${females === 1 ? 'female' : 'females'}`);
      if (males) breakdown.push(`${males} ${males === 1 ? 'male' : 'males'}`);
      const breakdownStr = breakdown.length ? ` (${breakdown.join(', ')})` : '';
      return `
        <div class="card" style="margin-bottom:0">
          <div style="font-family:'Libre Baskerville',serif;font-size:17px;color:var(--cream);margin-bottom:2px">${g.label /* esc-ok: fixed group name */}</div>
          <div style="font-size:12px;color:var(--muted);margin-bottom:14px">ages ${g.min}–${g.max} · ${members.length} ${members.length === 1 ? 'performer' : 'performers'}${breakdownStr}</div>
          ${members.length ? members.map(m => `
            <div style="display:flex;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid var(--border-soft)">
              <span style="flex:1;min-width:0;font-size:14px;color:var(--cream);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(m.name)}</span>
              <span style="font-size:13px;color:var(--muted);flex-shrink:0">${m.age}</span>
              <span style="font-size:13px;color:var(--muted);flex-shrink:0;width:54px;text-align:right">${m.gender ? esc(m.gender) : '—'}</span>
            </div>`).join('') : `<p class="empty" style="padding:12px 0">No performers in this group yet.</p>`}
        </div>`;
    };
    return `
      ${summaryRow}
      <div style="display:flex;justify-content:space-between;align-items:center;gap:16px;margin-bottom:18px;flex-wrap:wrap">
        ${viewToggle || '<span></span>'}
        ${printBtn}
      </div>
      <div style="font-size:13px;color:var(--muted);margin-bottom:16px">${allRows.length} committed ${allRows.length === 1 ? 'performer' : 'performers'} across all groups</div>
      <div class="age-group-cols">
        ${PC_AGE_GROUPS.map(groupPanel).join('')}
      </div>`;
  }

  return `
    ${summaryRow}
    <div style="display:flex;justify-content:space-between;align-items:center;gap:16px;margin-bottom:18px;flex-wrap:wrap">
      ${viewToggle || '<span></span>'}
      ${printBtn}
    </div>
    <div class="card">
      <div class="card-hd">${esc(prog ? prog.name : '')} — Enrollment Status</div>
      ${rows.length ? `<div class="tbl-wrap" style="margin-top:4px"><table>
        <thead><tr>
          ${th('name','Performer')}
          ${th('family','Family')}
          ${th('source','Source')}
          ${th('approved','Approved')}
          ${th('contract','Contract')}
          ${th('fee','Materials fee')}
          ${th('sent','Sent')}
          <th></th>
        </tr></thead>
        <tbody>
          ${rows.map((r, i) => {
            // Contract actions are per-family-per-program. Only show the action on the
            // first row for a given family contract so siblings don't duplicate the button.
            const firstForFamily = rows.findIndex(x => x.familyKey === r.familyKey) === i;
            let action = '';
            if (canEdit(session) && firstForFamily && r.approved && !r.contractSigned) {
              action = r.contractSent
                ? `<button class="btn-ghost btn-sm" data-action="remind-contract" data-family="${r.familyKey}" data-program="${esProgramId}">Send reminder</button>`
                : `<button class="btn-ghost btn-sm" data-action="send-contract" data-family="${r.familyKey}" data-program="${esProgramId}">Send contract</button>`;
              // Declining an already-approved family is new-family-only: returning
              // families never went through an approval step to decline out of.
              if (r.source === 'New') {
                action += ` <button class="btn-ghost btn-sm btn-danger" data-action="decline-contract" data-family="${r.familyKey}" data-program="${esProgramId}">Decline</button>`;
              }
            }
            return `<tr>
            <td>${r.performerId ? `<button class="tbl-link" onclick="viewPerformer('${r.performerId}')">${esc(r.name)}</button>` : esc(r.name)}<span class="sub" style="margin-left:8px">${r.gender ? esc(r.gender) + ' · ' : ''}${r.age != null ? 'age ' + r.age : ''}</span></td>
            <td style="color:var(--muted)">${r.accountId ? `<button class="tbl-link" onclick="viewParent('${r.accountId}')">${esc(r.family)}</button>` : esc(r.family)}</td>
            <td><span class="pill pill-off" style="font-size:10px">${r.source}</span></td>
            <td>${stageCell(r.approved, 'approved', '—')}</td>
            <td>${r.contractSigned ? stageCell(true,'signed') : (r.contractSent ? `<span class="pill pill-off" style="font-size:11px">awaiting signature</span>` : `<span class="pill pill-off" style="font-size:11px">not sent</span>`)}</td>
            <td>${stageCell(r.feePaid, 'paid', 'unpaid')}</td>
            <td style="white-space:nowrap">${(() => {
              if (r.contractSigned && r.feePaid) return '<span style="color:var(--muted)">—</span>';
              if (!r.sentDate) return '<span style="color:var(--muted)">—</span>';
              const d = new Date(r.sentDate.slice(0,10) + 'T00:00');
              const days = Math.floor((TODAY - d) / 86400000);
              const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              const remindedLine = r.lastRemindedAt
                ? `<div class="sub" style="margin-top:2px">Reminded ${new Date(r.lastRemindedAt.slice(0,10) + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>`
                : '';
              return `<span style="font-size:13px">Day ${days}</span><span class="sub" style="margin-left:5px">${label}</span>${remindedLine}`;
            })()}</td>
            <td style="text-align:right;white-space:nowrap">${action}</td>
          </tr>`;
          }).join('')}
        </tbody>
      </table></div>` : `<p class="empty">No performers registered for this program yet.</p>`}
    </div>`;
}

function setReviewDecision(regId, childIndex, decision) {
  if (!reviewDraft[regId]) reviewDraft[regId] = {};
  reviewDraft[regId][childIndex] = decision;
  refresh();
}

async function submitReview(regId) {
  const r = DB.registrations.find(x => x.id === regId);
  if (!r) return;
  const draft = reviewDraft[regId] || {};
  const pendingIdx = r.children.map((c, i) => c.status === 'pending' ? i : -1).filter(i => i >= 0);
  const undecided = pendingIdx.filter(i => !draft[i]);
  if (undecided.length) {
    const errEl = document.getElementById('reviewErr-' + regId);
    if (errEl) {
      errEl.textContent = pendingIdx.length > 1
        ? 'Please select an action for all performers before submitting.'
        : 'Please select an action for this performer before submitting.';
      errEl.style.display = 'block';
    }
    return;
  }
  // Build decisions keyed by registration_performer ID
  const decisions = {};
  r.children.forEach((ch, i) => {
    if (ch.status === 'pending' && draft[i]) decisions[ch.id] = draft[i];
  });
  const btn = document.getElementById('reviewBtn-' + regId);
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; btn.style.cursor = 'wait'; btn.textContent = 'Submitting…'; }
  try {
    const result = await apiFetch('/registrations/' + regId + '/review', {
      method: 'POST', body: JSON.stringify({ decisions })
    });
    if (result.registration) {
      const idx = DB.registrations.findIndex(x => x.id === regId);
      if (idx >= 0) DB.registrations[idx] = result.registration;
    }
    delete reviewDraft[regId];
    refresh();
    if (result.contractWarnings && result.contractWarnings.length) {
      result.contractWarnings.forEach(w => toast(w, 'error'));
    }
    toast(result.message || 'Decisions submitted');
  } catch (e) {
    if (btn) { btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'; btn.textContent = 'Submit decisions'; }
    toastError(e.message);
  }
}

// Group approved performers by program; create one contract per program not already created.
function buildContractsForApproved(r) {
  if (!r.contracts) r.contracts = [];
  const approvedByProgram = {};
  r.children.filter(c => c.status === 'approved').forEach(ch => {
    (approvedByProgram[ch.programInterest] = approvedByProgram[ch.programInterest] || []).push(ch.firstName + ' ' + ch.lastName);
  });
  Object.entries(approvedByProgram).forEach(([programId, names]) => {
    const existing = r.contracts.find(c => c.programId === programId);
    if (existing) {
      existing.performerNames = names;  // refresh names if a waitlisted kid was later approved
    } else {
      // In production: PHP creates Firma signing request using DB.settings.firmaTemplates[programId]
      r.contracts.push({ programId, performerNames: names, sent: true, signed: false });
    }
  });
}

// Later decision on a single waitlisted performer (per-performer email).
function decideWaitlisted(regId, childIndex, decision) {
  const r = DB.registrations.find(x => x.id === regId);
  if (!r) return;
  const ch = r.children[childIndex];
  if (!ch) return;
  // Declines get an explicit confirmation that names exactly who is affected
  if (decision === 'declined') {
    const prog = PROGRAMS.find(p => p.id === ch.programInterest);
    openModal(`
      <h3>Decline registration</h3>
      <p class="card-intro">You're about to decline this registration. The parent will receive an email letting them know.</p>
      <div style="background:rgba(255,255,255,0.03);border:1px solid var(--border-soft);border-radius:8px;padding:14px 16px;margin-bottom:18px;font-size:13px;line-height:1.9">
        <div><span style="color:var(--muted)">Parent:</span> <span style="color:var(--cream)">${esc(r.parent.firstName)} ${esc(r.parent.lastName)}</span></div>
        <div><span style="color:var(--muted)">Performer:</span> <span style="color:var(--cream)">${esc(ch.firstName)} ${esc(ch.lastName)}</span></div>
        <div><span style="color:var(--muted)">Program:</span> <span style="color:var(--cream)">${esc(prog ? prog.name : ch.programInterest)}</span></div>
      </div>
      <div style="display:flex;gap:10px">
        <button class="btn" id="waitlistDeclineBtn" style="flex:1;background:#f87171">Decline registration</button>
        <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
      </div>`, true);
    document.getElementById('waitlistDeclineBtn').addEventListener('click', () => applyWaitlistDecision(regId, childIndex, 'declined'));
    return;
  }
  applyWaitlistDecision(regId, childIndex, decision);
}

async function applyWaitlistDecision(regId, childIndex, decision) {
  const r = DB.registrations.find(x => x.id === regId);
  if (!r) return;
  const ch = r.children[childIndex];
  if (!ch) return;
  try {
    const result = await apiFetch('/registrations/' + regId + '/performers/' + ch.id + '/decide', {
      method: 'POST', body: JSON.stringify({ decision })
    });
    ch.status = decision;
    if (decision === 'approved') buildContractsForApproved(r);
    closeModal();
    refresh();
    if (result.warning) toast(result.warning, 'error');
    toast(result.message || `${ch.firstName} ${ch.lastName} — decision applied`);
  } catch (e) { toastError(e.message); }
}
